import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { StyleSheet, View } from 'react-native';

import { useActivatePromotionMutation } from 'generated/graphql-gateway';
import { ISystemwidePromotionMessageQuery } from 'generated/sanity-graphql';
import { PromotionActivationButton } from 'pages/loyalty/loyalty-promo-message-ribbon/promotion-activation-button';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { ZIndex, primitive, spacing } from 'styles/constants';
import logger from 'utils/logger';

interface ActivatablePromoRibbonProps {
  message: React.ReactElement;
  loyaltyId: string;
  promotionId: string;
  userActivatedAt?: string | null;
  onActivated: () => void;
  autoActivate?: boolean;
  sanityPromotion?: ISystemwidePromotionMessageQuery['SystemwidePromotion'];
}

const logPromoActivatedEvent = (
  promotion: ISystemwidePromotionMessageQuery['SystemwidePromotion'],
  status: 'Successful' | 'Failed'
) => {
  logRBIEvent({
    name: CustomEventNames.PROMO_ACTIVATED,
    type: EventTypes.Other,
    attributes: {
      name: promotion?.name,
      loyaltyEngineId: promotion?.loyaltyEngineId,
      status,
    },
  });
};

export const ActivatablePromoRibbon: React.FC<ActivatablePromoRibbonProps> = ({
  message,
  loyaltyId,
  promotionId,
  userActivatedAt,
  onActivated,
  autoActivate,
  sanityPromotion,
}) => {
  const [activatePromotion] = useActivatePromotionMutation();
  const [isActivating, setIsActivating] = useState(false);
  const hasAutoActivatedRef = useRef(false);

  const handleActivatePromotion = useCallback(async () => {
    setIsActivating(true);
    try {
      await activatePromotion({ variables: { loyaltyId, promotionId } });

      logPromoActivatedEvent(sanityPromotion, 'Successful');
    } catch (error) {
      logger.error({ error, message: 'Error activating promotion' });
      logPromoActivatedEvent(sanityPromotion, 'Failed');
    } finally {
      setIsActivating(false);
    }

    await onActivated();
  }, [activatePromotion, loyaltyId, promotionId, onActivated, sanityPromotion]);

  useEffect(() => {
    if (autoActivate && !userActivatedAt && !hasAutoActivatedRef.current) {
      hasAutoActivatedRef.current = true;
      handleActivatePromotion();
    }
  }, [autoActivate, userActivatedAt, handleActivatePromotion]);

  const allowActivation = !userActivatedAt;

  return (
    <View style={[styles.ribbonContent, userActivatedAt ? styles.activatedContent : undefined]}>
      <View style={styles.ribbonWrapper}>
        <View style={styles.textWrapper}>
          <Icon
            variant={userActivatedAt ? 'checkFill' : 'bkCrown'}
            size="$4"
            style={styles.promotionIcon}
            color={userActivatedAt ? primitive.$bbqBrown : primitive.$meltyYellow}
          />
          {message}
        </View>
        {allowActivation && (
          <PromotionActivationButton onPress={handleActivatePromotion} isLoading={isActivating} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  ribbonContent: {
    zIndex: ZIndex.NORMAL,
    backgroundColor: primitive.$meltyYellowOpacity20,
    paddingHorizontal: spacing.$3,
    height: spacing.$10,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  activatedContent: {
    backgroundColor: primitive.$successLight,
  },
  ribbonWrapper: {
    zIndex: ZIndex.MAX,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    gap: spacing.$2,
    width: '100%',
  },
  textWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: spacing.$1,
    flex: 1,
  },
  promotionIcon: {
    width: primitive.fontSizes.md,
    flexShrink: 0,
  },
});
